import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'api/axiosBaseQuery';
import {
  DownloadRequest,
  DownloadTask,
  DownloadTaskCreated,
  DownloadUrl,
  FilterOptions,
  toDownloadTaskCreated,
  toDownloadTaskList,
  toDownloadUrl,
  ValidateResult,
} from './types';

export const downloadTasksApi = createApi({
  reducerPath: 'downloadTasksApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['DownloadTasks'],
  endpoints: builder => ({
    // Lookup options for the filter dropdowns (camelCase response).
    getFilterOptions: builder.query<FilterOptions, void>({
      query: () => ({ url: 'v1/dataDownload/filters', method: 'GET' }),
    }),

    // Pre-query row-count estimate. `valid: false` means the query exceeds the
    // microservice's configured row limit — block submission and show message.
    validateDownload: builder.mutation<ValidateResult, DownloadRequest>({
      query: request => ({
        url: 'v1/dataDownload/validate',
        method: 'POST',
        data: request,
      }),
    }),

    // Submit a download task. Practice scoping / authorization is entirely
    // server-side from the caller's OOClientKey claims — no domain param.
    startDownloadTask: builder.mutation<DownloadTaskCreated, DownloadRequest>({
      query: request => ({
        url: 'v1/dataDownload/tasks',
        method: 'POST',
        data: request,
      }),
      transformResponse: toDownloadTaskCreated,
      invalidatesTags: [{ type: 'DownloadTasks', id: 'LIST' }],
    }),

    // The caller's task history, newest first (PascalCase response today).
    getDownloadTasks: builder.query<DownloadTask[], void>({
      query: () => ({ url: 'v1/dataDownload/tasks', method: 'GET' }),
      transformResponse: toDownloadTaskList,
      providesTags: [{ type: 'DownloadTasks', id: 'LIST' }],
    }),

    // Short-lived SAS URL for a completed task's CSV (PascalCase response).
    getDownloadUrl: builder.query<DownloadUrl, string>({
      query: taskId => ({
        url: `v1/dataDownload/tasks/${taskId}/downloadUrl`,
        method: 'GET',
      }),
      transformResponse: toDownloadUrl,
    }),
  }),
});

export const {
  useGetFilterOptionsQuery,
  useValidateDownloadMutation,
  useStartDownloadTaskMutation,
  useGetDownloadTasksQuery,
  useLazyGetDownloadUrlQuery,
} = downloadTasksApi;
